// Minnesotaänger Mini App — entry point (trimmed from telegram-tt, see PROVENANCE).
import './util/handleError';
import './global/init';

import TeactDOM from './lib/teact/teact-dom';
import { getActions } from './global';

import {
  DEBUG,
} from './config';
import { requestMutation } from './lib/fasterdom/fasterdom';
import { initGlobal } from './util/init';
import { initAppEnvironment } from './util/initAppEnvironment';
import { setBootStage } from './util/bootStage';
import { initTelegramFullscreen, type TgWebAppLike } from './util/fullscreen';
// v1: onBeforeUnload import removed — unload handling was trimmed with group calls.

import App from './components/App';
import { MINIAPP_API_BASE } from './api/miniapp/client';

import './assets/fonts/roboto.css';
import './styles/index.scss';

// v1: boot diagnostics — 模块体开始求值即推进探针阶段链
// （阶段定义与显示见 public/boot-diag.js）。
setBootStage('module-evaluating');

// v1: build revision for support — the exact commit this bundle was built
// from is queryable at runtime (window.__MINIAPP_BUILD_SHA__, injected by
// vite.config.ts from git HEAD) and is shown in the boot-error fallback.
(window as any).__MINIAPP_BUILD_SHA__ = import.meta.env.VITE_BUILD_SHA || 'unknown';

// v1: runtime-queryable API base — the actual endpoint this bundle talks to
// (derived from BASE_URL unless an explicit VITE_API_BASE was given at build
// time). Lets support verify what the deployed bundle points at.
(window as any).__MINIAPP_API_BASE__ = MINIAPP_API_BASE;

init().catch((err) => {
  // eslint-disable-next-line no-console
  console.error('[miniapp] init failed:', err);
  showBootError(err instanceof Error ? `${err.message}\n${err.stack || ''}` : String(err));
});

// v1: boot error fallback — a startup failure must never leave a pure white
// screen (test environment, no styling needed). Three guards:
// 1. window error / unhandledrejection before the first render.
// 2. init() rejection (above).
// 3. a watchdog: if #Main has not appeared 15s after load, the boot chain
//    died silently (e.g. a boot script missing) — report instead of blank.
function showBootError(message: string) {
  const root = document.getElementById('root');
  if (!root) return;

  const el = document.createElement('div');
  el.style.cssText = 'padding:1rem;font:14px/1.5 system-ui,sans-serif;'
    + 'color:#fff;background:#1f1f1f;white-space:pre-wrap;word-break:break-all';
  el.textContent = `Mini App boot error\nbuild: ${(window as any).__MINIAPP_BUILD_SHA__}\n${message}`;
  root.replaceChildren(el);
}

const handleBootError = (event: ErrorEvent | PromiseRejectionEvent) => {
  const message = event instanceof PromiseRejectionEvent
    ? (event.reason && (event.reason.stack || String(event.reason)))
    : event.message;
  if (!message) return;

  if (document.getElementById('Main')) {
    // Business UI is up — its own handlers take over. Do not replace the
    // page; surface the error on the boot diagnostic overlay instead.
    // eslint-disable-next-line no-console
    console.error('[miniapp] boot error after Main mounted:', message);
    (window as any).__miniappBootDiagRecordError?.(String(message));
    return;
  }

  showBootError(String(message));
};
window.addEventListener('error', handleBootError);
window.addEventListener('unhandledrejection', handleBootError);
window.setTimeout(() => {
  if (!document.getElementById('Main')) {
    const message = 'App did not finish booting (no #Main within 15s). '
      + 'Check that telegram-web-app.js / boot scripts loaded.';
    (window as any).__miniappBootDiagRecordError?.('boot watchdog: ' + message);
    showBootError(message);
  }
}, 15000);

async function init() {
  setBootStage('init-start');

  // v1 (fix-list B/J): WebView 标准调用（ready/expand）+ real Mini App
  // fullscreen，镜像到 <html>.tg-fullscreen。逻辑在 util/fullscreen.ts —
  // 纯模块，无 SDK / 不支持客户端的回退有单测
  // （tests/contract/fullscreen-fallback.test.ts）。
  const tgWebApp = (window as typeof window & {
    Telegram?: { WebApp?: TgWebAppLike };
  }).Telegram?.WebApp;
  initTelegramFullscreen(tgWebApp);

  await initGlobal();
  setBootStage('global-ready');

  // v1: restores the UI environment telegram-tt initialized in
  // ui/initial.ts + App.tsx (theme class/colors, body platform classes,
  // text-size variables, wallpaper background) — before first render so the
  // first frame is already themed.
  initAppEnvironment();
  setBootStage('environment-ready');

  getActions().init();

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    setBootStage('render-requested');

    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );
  });

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }
}

// v1: telegram-tt ended group calls / audio player on unload — both trimmed.
